/**
 * @fileoverview ParcelPuffin POS Terms of Service registry data.
 *
 * Hand-migrated faithfully from
 * `apps/pos-web/src/portals/marketing-website/pages/TermsOfServicePage.jsx`.
 *
 * Includes pos-only sections (SMS Communications Consent, Subscription
 * and Billing, Payment Processing, Service Availability,
 * Indemnification) reflecting full POS-domain terms surface.
 */

/** @type {import('../../types.js').LegalDocument} */
export const terms = Object.freeze({
  appKey: 'parcel',
  documentKey: 'terms',
  frontmatter: {
    title: 'Terms of Service',
    productName: 'ParcelPuffin POS',
    legalEntity: 'D Town Retail, LLC',
    effectiveDate: '2025-12-17',
    contactEmail: 'legal@parcelpuffin.com',
    canonicalUrl: 'https://parcelpuffin.com/terms',
    ogImage: 'https://parcelpuffin.com/images/og-terms.jpg',
    governingLaw: 'Delaware',
    description:
      'Terms of service for ParcelPuffin POS. Read our terms and conditions for using our point-of-sale software.',
  },
  sections: [
    {
      id: 'agreement-to-terms',
      title: '1. Agreement to Terms',
      order: 1,
      blocks: [
        {
          type: 'paragraph',
          text: 'By accessing or using {productName} (the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Service.',
        },
        {
          type: 'paragraph',
          text: 'These Terms apply to all visitors, users, and others who access or use the Service. By using the Service, you represent that you are at least 18 years of age and have the legal authority to enter into these Terms.',
        },
      ],
    },
    {
      id: 'description-of-service',
      title: '2. Description of Service',
      order: 2,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} is a cloud-based point-of-sale software solution designed for print shops, shipping centers, and retail operations. The Service includes features for:',
        },
        {
          type: 'list',
          items: [
            'Point-of-sale transaction processing',
            'Inventory management',
            'Customer management and loyalty programs',
            'Employee time tracking and management',
            'Shipping and mailbox services',
            'Printing and document services',
            'Reporting and analytics',
          ],
        },
      ],
    },
    {
      id: 'account-registration',
      title: '3. Account Registration',
      order: 3,
      blocks: [
        {
          type: 'paragraph',
          text: 'To use certain features of the Service, you must register for an account. When you register, you agree to:',
        },
        {
          type: 'list',
          items: [
            'Provide accurate, current, and complete information',
            'Maintain and promptly update your account information',
            'Maintain the security of your password and account',
            'Accept responsibility for all activities under your account',
            'Notify us immediately of any unauthorized access',
          ],
        },
        {
          type: 'paragraph',
          text: 'We reserve the right to suspend or terminate accounts that contain false or misleading information or are used in violation of these Terms.',
        },
      ],
    },
    {
      id: 'sms-communications-consent',
      title: '4. SMS Communications Consent',
      order: 4,
      blocks: [
        {
          type: 'paragraph',
          text: 'By providing your phone number during registration, you expressly consent to receive text messages from {productName} for:',
        },
        {
          type: 'list',
          items: [
            {
              strong: 'Account Verification',
              text: 'One-time verification codes for account security',
            },
            {
              strong: 'Security Alerts',
              text: 'Notifications about security events or suspicious activity',
            },
            {
              strong: 'Billing Notifications',
              text: 'Important updates about your subscription and payments',
            },
            {
              strong: 'Service Updates',
              text: 'Critical information about changes to the Service',
            },
          ],
        },
        {
          type: 'paragraph',
          text: 'Message and data rates may apply. Message frequency varies based on your account activity. You may opt out of SMS communications by contacting support, but this may limit certain security features of your account.',
        },
        {
          type: 'paragraph',
          text: 'By consenting to receive SMS messages, you confirm that you are the owner or authorized user of the phone number provided.',
        },
      ],
    },
    {
      id: 'subscription-and-billing',
      title: '5. Subscription and Billing',
      order: 5,
      blocks: [
        {
          type: 'paragraph',
          text: 'Access to the Service requires a paid subscription. By subscribing, you agree to:',
        },
        {
          type: 'list',
          items: [
            'Pay all fees associated with your selected plan',
            'Provide valid payment information',
            'Authorize us to charge your payment method on a recurring basis',
          ],
        },
        {
          type: 'paragraph',
          text: 'Subscription fees are billed in advance on a monthly or annual basis depending on your selected plan. All fees are non-refundable except as expressly stated in these Terms or required by law.',
        },
        {
          type: 'paragraph',
          text: "We reserve the right to change subscription fees upon 30 days' notice. Continued use of the Service after a price change constitutes acceptance of the new pricing.",
        },
      ],
    },
    {
      id: 'acceptable-use',
      title: '6. Acceptable Use',
      order: 6,
      blocks: [
        { type: 'paragraph', text: 'You agree not to use the Service to:' },
        {
          type: 'list',
          items: [
            'Violate any applicable laws or regulations',
            'Process fraudulent or unauthorized transactions',
            'Infringe on intellectual property rights of others',
            'Transmit viruses, malware, or other harmful code',
            'Attempt to gain unauthorized access to our systems',
            'Interfere with or disrupt the Service',
            'Collect information about other users without consent',
            'Use the Service for any illegal or unauthorized purpose',
          ],
        },
      ],
    },
    {
      id: 'intellectual-property',
      title: '7. Intellectual Property',
      order: 7,
      blocks: [
        {
          type: 'paragraph',
          text: 'The Service and its original content, features, and functionality are owned by {productName} and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.',
        },
        {
          type: 'paragraph',
          text: 'You retain ownership of any data you submit to the Service. By submitting data, you grant us a limited license to use, store, and process that data solely to provide the Service to you.',
        },
      ],
    },
    {
      id: 'data-and-privacy',
      title: '8. Data and Privacy',
      order: 8,
      blocks: [
        {
          type: 'paragraph',
          text: 'Your use of the Service is also governed by our Privacy Policy, which is incorporated into these Terms by reference. Please review our Privacy Policy to understand how we collect, use, and protect your information.',
        },
        {
          type: 'paragraph',
          text: 'You are responsible for maintaining the confidentiality of your business data and for ensuring that your use of the Service complies with applicable data protection laws.',
        },
      ],
    },
    {
      id: 'payment-processing',
      title: '9. Payment Processing',
      order: 9,
      blocks: [
        {
          type: 'paragraph',
          text: 'The Service may integrate with third-party payment processors. By using payment processing features, you agree to comply with the terms and conditions of those third-party services.',
        },
        {
          type: 'paragraph',
          text: 'We are not responsible for any errors, delays, or issues arising from third-party payment processing services.',
        },
      ],
    },
    {
      id: 'service-availability',
      title: '10. Service Availability',
      order: 10,
      blocks: [
        {
          type: 'paragraph',
          text: 'We strive to maintain high availability of the Service but do not guarantee uninterrupted access. The Service may be temporarily unavailable due to:',
        },
        {
          type: 'list',
          items: [
            'Scheduled maintenance (with advance notice when possible)',
            'Emergency maintenance or repairs',
            'Factors outside our reasonable control',
          ],
        },
        {
          type: 'paragraph',
          text: 'We are not liable for any loss or damage resulting from Service unavailability.',
        },
      ],
    },
    {
      id: 'limitation-of-liability',
      title: '11. Limitation of Liability',
      order: 11,
      blocks: [
        {
          type: 'paragraph',
          text: 'TO THE MAXIMUM EXTENT PERMITTED BY LAW, {productName} SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.',
        },
        {
          type: 'paragraph',
          text: 'Our total liability for any claims under these Terms shall not exceed the amount you paid us in the twelve (12) months preceding the claim.',
        },
      ],
    },
    {
      id: 'indemnification',
      title: '12. Indemnification',
      order: 12,
      blocks: [
        {
          type: 'paragraph',
          text: "You agree to indemnify, defend, and hold harmless {productName} and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including attorneys' fees) arising from:",
        },
        {
          type: 'list',
          items: [
            'Your use of the Service',
            'Your violation of these Terms',
            'Your violation of any third-party rights',
            'Your business operations and transactions',
          ],
        },
      ],
    },
    {
      id: 'termination',
      title: '13. Termination',
      order: 13,
      blocks: [
        {
          type: 'paragraph',
          text: 'We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including breach of these Terms.',
        },
        {
          type: 'paragraph',
          text: 'Upon termination, your right to use the Service will cease immediately. You may export your data prior to termination. We may retain certain data as required by law or for legitimate business purposes.',
        },
      ],
    },
    {
      id: 'changes-to-terms',
      title: '14. Changes to Terms',
      order: 14,
      blocks: [
        {
          type: 'paragraph',
          text: 'We reserve the right to modify these Terms at any time. We will provide notice of material changes by posting the updated Terms on this page and updating the "Effective Date."',
        },
        {
          type: 'paragraph',
          text: 'Your continued use of the Service after changes become effective constitutes acceptance of the revised Terms.',
        },
      ],
    },
    {
      id: 'governing-law',
      title: '15. Governing Law',
      order: 15,
      blocks: [
        {
          type: 'paragraph',
          text: 'These Terms shall be governed by and construed in accordance with the laws of the State of {governingLaw}, United States, without regard to its conflict of law provisions.',
        },
        {
          type: 'paragraph',
          text: 'Any disputes arising under these Terms shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.',
        },
      ],
    },
    {
      id: 'contact-information',
      title: '16. Contact Information',
      order: 16,
      blocks: [
        {
          type: 'paragraph',
          text: 'If you have questions about these Terms of Service, please contact us at:',
        },
        { type: 'contact' },
      ],
    },
  ],
});
